import type { MemberPayment } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { PAYMENT_STATUS_LABELS } from '@/entities/payment';
import { formatDateTime, formatKrw, safeHttpsUrl } from '@/shared/lib';

interface QuotePaymentSummaryProps {
  readonly payment: MemberPayment;
}

/**
 * 결제 정보 — 견적 상세 응답에 실린 결제 요약(상태·금액·수단·승인 시각·영수증)만 보여 준다.
 * 영수증 링크는 내 주문 상세와 같은 규칙이다 — 승인되지 않은 결제(미결제·결제 전 취소)는 숨기고, https 주소만 연다.
 */
export const QuotePaymentSummary = ({ payment }: QuotePaymentSummaryProps): ReactElement => {
  const receiptUrl = payment.approvedAt !== null ? safeHttpsUrl(payment.receiptUrl) : null;
  return (
    <section aria-label="결제 정보" className="flex flex-col gap-3">
      <p
        aria-hidden
        className="text-[11px] leading-none font-semibold tracking-[0.18em] text-muted-foreground uppercase"
      >
        Payment
      </p>
      <dl className="divide-y divide-foreground/10 border-y border-foreground/10 text-sm">
        <div className="flex gap-4 py-3">
          <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">결제 상태</dt>
          <dd className="font-semibold">{PAYMENT_STATUS_LABELS[payment.status]}</dd>
        </div>
        <div className="flex gap-4 py-3">
          <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">결제 금액</dt>
          <dd className="tabular-nums">{formatKrw(payment.amount)}</dd>
        </div>
        {payment.method !== null ? (
          <div className="flex gap-4 py-3">
            <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">결제 수단</dt>
            <dd>{payment.method}</dd>
          </div>
        ) : null}
        {payment.approvedAt !== null ? (
          <div className="flex gap-4 py-3">
            <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">승인 일시</dt>
            <dd className="tabular-nums">{formatDateTime(payment.approvedAt)}</dd>
          </div>
        ) : null}
      </dl>
      {receiptUrl !== null ? (
        <a
          href={receiptUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-fit text-sm font-semibold underline underline-offset-4 hover:no-underline"
        >
          영수증 보기
        </a>
      ) : null}
    </section>
  );
};
